import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { XOR } from 'types';
import {
  DataRoomMember,
  DataRoomMemberRole,
} from 'router/subrouters/DataRoom/models/DataRoomMember';
import { QAndARoles } from 'router/subrouters/DataRoom/models/QAndA';
import RoleTag from 'router/subrouters/DataRoom/subrouters/components/RoleTag';
import {
  qaRoleTagColorMap,
  qaRoleTagLabelMap,
  roleTagColorMap,
  roleTagLabelMap,
} from 'router/subrouters/DataRoom/constants';
import { useTranslation } from 'react-i18next';
import { Dropdown } from 'ncoded-component-library';
import useCallbackRef from 'hooks/useCallbackRef';
import { DropdownRef } from 'ncoded-component-library/build/components/molecules/Dropdown/Dropdown.component';
import useOptions from 'router/subrouters/DataRoom/subrouters/Members/hooks/useOptions';
import useMatchMedia from 'hooks/useMatchMedia';
import useDropdownContentSize from 'hooks/useDropdownContentSize';

import './MemberRoleInstantChange.styles.scss';

type MemberRoleInstantChangeProps = {
  className?: string;
  memberId: string;
  isBuySide: boolean;
  onChange?: (memberId: string, updated: Partial<DataRoomMember>) => void;
} & XOR<{ role: DataRoomMemberRole }, { qaRole: QAndARoles }>;

const MemberRoleInstantChange: React.FC<MemberRoleInstantChangeProps> = (
  props,
) => {
  const { className, memberId, role, qaRole, isBuySide, onChange } = props;

  const [dropdown, dropdownRef] = useCallbackRef<DropdownRef>();

  const isPhablet = useMatchMedia('isPhablet');

  const { buyerRoleOptions, buyerQaRole, sellerRoleOptions, sellerQaRole } =
    useOptions();

  const { dropdownContentRef } = useDropdownContentSize(dropdown);

  const qaRoleOptions = useMemo(
    () => (isBuySide ? buyerQaRole : sellerQaRole),
    [buyerQaRole, isBuySide, sellerQaRole],
  );

  const roleOptions = useMemo(
    () => (isBuySide ? buyerRoleOptions : sellerRoleOptions),
    [buyerRoleOptions, isBuySide, sellerRoleOptions],
  );

  const canEdit = useMemo(
    () => !isPhablet && !!onChange,
    [isPhablet, onChange],
  );

  const classes = classNames('member-role-instant-change', className);

  const { t } = useTranslation();

  const Tag = useMemo(
    () => (
      <RoleTag
        className={classes}
        variant={role ? roleTagColorMap[role] : qaRoleTagColorMap[qaRole]}
      >
        {t(role ? roleTagLabelMap[role] : qaRoleTagLabelMap[qaRole])}
      </RoleTag>
    ),
    [classes, qaRole, role, t],
  );

  const closeDropdown = useCallback(
    () => dropdown?.setIsOpen(false),
    [dropdown],
  );

  const onChangeRole = useCallback(
    (value) => {
      onChange(memberId, role ? { role: value } : { qaRole: value });
      closeDropdown();
    },
    [memberId, role, closeDropdown, onChange],
  );

  // Rendered while closed too: the library picks a side by measuring the
  // content as it opens, and an empty box always fits below.
  const content = useMemo(
    () => (
      <div
        ref={dropdownContentRef}
        className="member-role-instant-change-dropdown"
      >
        {(role ? roleOptions : qaRoleOptions).map(
          ({ value, props: { title, description } }) => {
            const selected = role === value || qaRole === value;

            return (
              <div
                key={value}
                className={classNames('clickable', 'role-option', {
                  selected,
                })}
                onClick={() =>
                  selected ? closeDropdown() : onChangeRole(value)
                }
              >
                <span>{title}</span>
                {description && <span>{description}</span>}
              </div>
            );
          },
        )}
      </div>
    ),
    [
      dropdownContentRef,
      role,
      roleOptions,
      qaRoleOptions,
      qaRole,
      closeDropdown,
      onChangeRole,
    ],
  );

  if (!canEdit) return Tag;

  return (
    <Dropdown
      ref={dropdownRef}
      closeWithJS
      renderAsPortal
      className={classes}
      contentClassName="member-role-instant-change__content"
      trigger={Tag}
    >
      {content}
    </Dropdown>
  );
};

export default MemberRoleInstantChange;
